(() => {
  const rootTeardowns = new WeakMap();
  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const setupGallery = (gallery) => {
    if (gallery.dataset.hbReady === 'true') return;
    gallery.dataset.hbReady = 'true';

    const track = gallery.querySelector('[data-hb-track]');
    const slides = [...gallery.querySelectorAll('[data-hb-slide]')];
    const thumbnails = [...gallery.querySelectorAll('[data-hb-thumbnail]')];
    const previous = gallery.querySelector('[data-hb-prev]');
    const next = gallery.querySelector('[data-hb-next]');
    const status = gallery.querySelector('[data-hb-status]');
    const viewport = gallery.querySelector('.lmcd-hb-gallery__viewport');
    const reducedMotion = prefersReducedMotion();
    let activeIndex = 0;
    let pointerStart = null;

    if (track && reducedMotion) track.style.transition = 'none';

    const render = (nextIndex) => {
      activeIndex = Math.max(0, Math.min(nextIndex, slides.length - 1));
      if (track) track.style.transform = `translate3d(-${activeIndex * 100}%, 0, 0)`;
      slides.forEach((slide, index) => slide.setAttribute('aria-hidden', String(index !== activeIndex)));
      thumbnails.forEach((thumbnail, index) => {
        const selected = index === activeIndex;
        thumbnail.setAttribute('aria-selected', String(selected));
        thumbnail.classList.toggle('is-active', selected);
      });
      previous?.toggleAttribute('disabled', activeIndex === 0);
      next?.toggleAttribute('disabled', activeIndex === slides.length - 1);
      if (status) status.textContent = `${activeIndex + 1} / ${slides.length}`;
    };

    thumbnails.forEach((thumbnail, index) => thumbnail.addEventListener('click', () => render(index)));
    previous?.addEventListener('click', () => render(activeIndex - 1));
    next?.addEventListener('click', () => render(activeIndex + 1));
    gallery.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') render(activeIndex - 1);
      if (event.key === 'ArrowRight') render(activeIndex + 1);
    });
    viewport?.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      pointerStart = { id: event.pointerId, x: event.clientX, y: event.clientY };
    });
    viewport?.addEventListener('pointerup', (event) => {
      if (!pointerStart || pointerStart.id !== event.pointerId) return;
      const deltaX = event.clientX - pointerStart.x;
      const deltaY = event.clientY - pointerStart.y;
      pointerStart = null;
      if (Math.abs(deltaX) < 45 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
      render(activeIndex + (deltaX < 0 ? 1 : -1));
    });
    viewport?.addEventListener('pointercancel', () => { pointerStart = null; });
    render(0);
  };

  const setupDisclosures = (root) => {
    const panelSelector = '[data-hb-faq-panel], [data-hb-disclosure-panel], [data-faq-panel]';

    const findPanel = (button) => {
      const panelId = button.getAttribute('aria-controls');
      if (panelId) {
        const controlledPanel = [...root.querySelectorAll('[id]')].find((element) => element.id === panelId);
        if (controlledPanel) return controlledPanel;
      }

      const directPanel = button.nextElementSibling?.matches(panelSelector) ? button.nextElementSibling : null;
      if (directPanel) return directPanel;

      const item = button.closest('[data-hb-faq-item], [data-hb-product-item]')
        || button.closest('.lmcd-womans-heart__faq-item')
        || button.closest('article');
      return item?.querySelector(panelSelector) || null;
    };

    const setupDisclosure = (button) => {
      button.addEventListener('click', () => {
        const expanded = button.getAttribute('aria-expanded') === 'true';
        const panel = findPanel(button);
        button.setAttribute('aria-expanded', String(!expanded));
        if (panel) panel.hidden = expanded;
      });
    };

    root.querySelectorAll('[data-hb-faq], [data-hb-product-disclosure], [data-faq-trigger]').forEach(setupDisclosure);
  };

  const setupFooterDisclosures = (root, addTeardown) => {
    root.querySelectorAll('[data-footer-trigger]').forEach((trigger) => {
      const panel = root.querySelector(`#${trigger.getAttribute('aria-controls')}`);
      if (!panel) return;
      let mobile;
      const update = () => {
        const nextMobile = window.innerWidth < 768;
        if (nextMobile === mobile) return;
        mobile = nextMobile;
        panel.hidden = mobile;
        trigger.setAttribute('aria-expanded', String(!mobile));
      };
      trigger.addEventListener('click', () => {
        if (!mobile) return;
        panel.hidden = !panel.hidden;
        trigger.setAttribute('aria-expanded', String(!panel.hidden));
      });
      window.addEventListener('resize', update);
      addTeardown(() => window.removeEventListener('resize', update));
      update();
    });
  };

  const setupOlivaDetails = (root, addTeardown) => {
    const reducedMotion = prefersReducedMotion();
    const proof = root.querySelector('.lmcd-womans-heart__proof-images');
    if (proof && !reducedMotion && typeof proof.animate === 'function') {
      const animation = proof.animate(
        [{ transform: 'translateX(0)' }, { transform: `translateX(-${proof.scrollWidth / 3}px)` }],
        { duration: 16000, iterations: Infinity, easing: 'linear' }
      );
      addTeardown(() => animation.cancel());
    }
    root.querySelectorAll('a[href^="#lmcd-hero-bible-"]').forEach((link) => {
      link.addEventListener('click', (event) => {
        const target = root.querySelector(link.getAttribute('href'));
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
      });
    });
    const countdown = root.querySelector('[data-countdown]');
    if (countdown) {
      let remaining = 15 * 60;
      const countdownTimer = window.setInterval(() => {
        remaining = Math.max(remaining - 1, 0);
        countdown.textContent = `${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, '0')}`;
      }, 1000);
      addTeardown(() => window.clearInterval(countdownTimer));
    }
  };

  const setupCarousels = (root, addTeardown) => {
    const reducedMotion = prefersReducedMotion();

    root.querySelectorAll('[data-carousel]').forEach((carousel) => {
      if (carousel.dataset.hbCarouselReady === 'true') return;
      carousel.dataset.hbCarouselReady = 'true';

      const track = carousel.querySelector('[data-carousel-track]');
      const previous = carousel.querySelector('[data-carousel-prev]');
      const next = carousel.querySelector('[data-carousel-next]');
      if (!track) return;

      const updateButtons = () => {
        const maximumScroll = Math.max(0, track.scrollWidth - track.clientWidth);
        const scrollPosition = Math.abs(track.scrollLeft);
        if (previous) previous.disabled = scrollPosition <= 1;
        if (next) next.disabled = maximumScroll <= 1 || scrollPosition >= maximumScroll - 1;
      };

      const move = (direction) => {
        const firstItem = track.firstElementChild;
        if (!firstItem) return;
        const gap = parseFloat(window.getComputedStyle(track).gap) || 0;
        const distance = firstItem.getBoundingClientRect().width + gap;
        track.scrollBy({ left: direction * distance, behavior: reducedMotion ? 'auto' : 'smooth' });
      };

      previous?.addEventListener('click', () => move(-1));
      next?.addEventListener('click', () => move(1));
      track.addEventListener('scroll', updateButtons, { passive: true });
      track.addEventListener('keydown', (event) => {
        if (event.target !== track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        move(event.key === 'ArrowRight' ? 1 : -1);
      });
      window.addEventListener('resize', updateButtons);
      addTeardown(() => window.removeEventListener('resize', updateButtons));
      if (typeof ResizeObserver === 'function') {
        const resizeObserver = new ResizeObserver(updateButtons);
        resizeObserver.observe(track);
        addTeardown(() => resizeObserver.disconnect());
      }
      updateButtons();
      window.requestAnimationFrame?.(updateButtons);
    });
  };

  const setupMobileMenu = (root, addTeardown) => {
    const trigger = root.querySelector('[data-hb-menu-trigger]');
    const menu = root.querySelector('[data-hb-mobile-menu]');
    if (!trigger || !menu) return;

    const setMenuOpen = (open) => {
      menu.hidden = !open;
      trigger.setAttribute('aria-expanded', String(open));
      trigger.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
    };

    trigger.addEventListener('click', (event) => {
      event.stopPropagation();
      setMenuOpen(trigger.getAttribute('aria-expanded') !== 'true');
    });
    menu.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => setMenuOpen(false)));
    const handleKeydown = (event) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    const handleDocumentClick = (event) => {
      if (trigger.getAttribute('aria-expanded') === 'true' && !trigger.contains(event.target) && !menu.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeydown);
    document.addEventListener('click', handleDocumentClick);
    addTeardown(() => document.removeEventListener('keydown', handleKeydown));
    addTeardown(() => document.removeEventListener('click', handleDocumentClick));
    setMenuOpen(false);
  };

  const setupStickyPurchase = (root, addTeardown) => {
    const form = root.querySelector('[data-hb-primary-form] form, form[data-hb-primary-form], #lmcd-hb-primary-form');
    const bar = root.querySelector('[data-hb-sticky-cta]');
    const button = root.querySelector('[data-hb-sticky-purchase]');
    const footer = root.querySelector('[data-hb-footer]');
    if (!form || !bar || !button) return;

    const wrapper = root.closest('.page-wrapper') || document.querySelector('.page-wrapper');
    const wrapperScrolls = () => {
      if (!wrapper) return false;
      const overflowY = window.getComputedStyle(wrapper).overflowY;
      return /auto|scroll|overlay/.test(overflowY) && wrapper.scrollHeight > wrapper.clientHeight + 1;
    };

    const update = () => {
      const scrollsInWrapper = wrapperScrolls();
      const wrapperBounds = scrollsInWrapper ? wrapper.getBoundingClientRect() : null;
      const viewportTop = wrapperBounds ? wrapperBounds.top : 0;
      const viewportBottom = wrapperBounds ? wrapperBounds.bottom : window.innerHeight;
      const offerPast = form.getBoundingClientRect().bottom <= viewportTop;
      const nearFooter = footer && footer.getBoundingClientRect().top <= viewportBottom + 100;
      bar.hidden = !offerPast || Boolean(nearFooter);
    };

    button.addEventListener('click', () => {
      if (button.disabled) return;
      if (typeof form.requestSubmit === 'function') form.requestSubmit();
      else form.querySelector('[type="submit"]')?.click();
    });
    wrapper?.addEventListener('scroll', update, { passive: true });
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    addTeardown(() => wrapper?.removeEventListener('scroll', update));
    addTeardown(() => window.removeEventListener('scroll', update));
    addTeardown(() => window.removeEventListener('resize', update));
    update();
  };

  const pauseOtherVideos = (root, activeVideo) => {
    root.querySelectorAll('[data-hb-video] video').forEach((video) => {
      if (video === activeVideo) return;
      video.pause();
      video.muted = true;
      const frame = video.closest('[data-hb-video]');
      const toggle = frame?.querySelector('[data-hb-sound-toggle]');
      const label = frame?.querySelector('[data-hb-sound-label]');
      toggle?.setAttribute('aria-pressed', 'false');
      if (toggle) toggle.setAttribute('aria-label', 'Play video with sound');
      if (label) label.textContent = 'Tap for sound';
    });
  };

  const setupVideoAudio = (root, addTeardown) => {
    const reducedMotion = prefersReducedMotion();
    const frames = [...root.querySelectorAll('[data-hb-video]')];
    const visibilityObserver = !reducedMotion && typeof IntersectionObserver === 'function'
      ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const video = entry.target.querySelector('video');
          if (!video) return;
          if (entry.isIntersecting) {
            if (video.paused) video.play().catch(() => {});
          } else {
            if (!video.paused) video.pause();
            video.muted = true;
            const toggle = entry.target.querySelector('[data-hb-sound-toggle]');
            const label = entry.target.querySelector('[data-hb-sound-label]');
            toggle?.setAttribute('aria-pressed', 'false');
            if (toggle) toggle.setAttribute('aria-label', 'Play video with sound');
            if (label) label.textContent = 'Tap for sound';
          }
        });
      }, { rootMargin: '400px 0px' })
      : null;
    if (visibilityObserver) addTeardown(() => visibilityObserver.disconnect());

    frames.forEach((frame) => {
      if (frame.dataset.hbVideoReady === 'true') return;
      const video = frame.querySelector('video');
      const toggle = frame.querySelector('[data-hb-sound-toggle]');
      const label = frame.querySelector('[data-hb-sound-label]');
      if (!video || !toggle) return;
      frame.dataset.hbVideoReady = 'true';

      video.autoplay = false;
      video.loop = true;
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.removeAttribute?.('autoplay');
      video.setAttribute('loop', '');
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');

      const renderAudioState = () => {
        const soundOn = !video.muted && !video.paused;
        toggle.setAttribute('aria-pressed', String(soundOn));
        toggle.setAttribute('aria-label', soundOn ? 'Mute video' : 'Play video with sound');
        if (label) label.textContent = soundOn ? 'Mute' : 'Tap for sound';
      };
      const coordinateAudioState = () => {
        if (!video.muted && !video.paused) pauseOtherVideos(root, video);
        renderAudioState();
      };
      const toggleSound = async () => {
        if (!video.muted && !video.paused) {
          video.muted = true;
          renderAudioState();
          return;
        }

        pauseOtherVideos(root, video);
        video.muted = false;
        await video.play().catch(() => {});
        renderAudioState();
      };

      toggle.addEventListener('click', toggleSound);
      video.addEventListener('play', coordinateAudioState);
      video.addEventListener('volumechange', coordinateAudioState);
      video.addEventListener('pause', renderAudioState);
      video.pause();
      visibilityObserver?.observe(frame);
      renderAudioState();
    });
  };

  const setupReviewMasonry = (root, addTeardown) => {
    const grid = root.querySelector('.lmcd-hero-bible__review-grid');
    if (!grid || grid.dataset.hbMasonryReady === 'true') return;
    const cards = [...grid.querySelectorAll('[data-hb-customer-review]')];
    if (!cards.length) return;
    grid.dataset.hbMasonryReady = 'true';

    const layout = () => {
      const columns = window.innerWidth <= 640 ? 2 : window.innerWidth <= 900 ? 3 : 5;
      const columnWidth = grid.clientWidth / columns;
      if (!Number.isFinite(columnWidth) || columnWidth <= 0) return;
      const heights = Array(columns).fill(0);

      cards.forEach((card) => {
        const column = heights.indexOf(Math.min(...heights));
        card.style.position = 'absolute';
        card.style.left = `${column * columnWidth}px`;
        card.style.top = `${heights[column]}px`;
        card.style.width = `${columnWidth}px`;
        heights[column] += card.getBoundingClientRect().height;
      });
      grid.style.height = `${Math.max(...heights)}px`;
    };

    let layoutFrame;
    const scheduleLayout = () => {
      if (layoutFrame) window.cancelAnimationFrame?.(layoutFrame);
      layoutFrame = window.requestAnimationFrame(layout);
    };
    cards.forEach((card) => card.querySelectorAll('img,video').forEach((media) => {
      if (!media.complete || media.tagName === 'VIDEO') media.addEventListener('load', scheduleLayout, { once: true });
      if (media.tagName === 'VIDEO') media.addEventListener('loadedmetadata', scheduleLayout, { once: true });
    }));
    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(scheduleLayout);
      resizeObserver.observe(grid);
      addTeardown(() => resizeObserver.disconnect());
    } else {
      window.addEventListener('resize', scheduleLayout);
      addTeardown(() => window.removeEventListener('resize', scheduleLayout));
    }
    addTeardown(() => {
      if (layoutFrame) window.cancelAnimationFrame?.(layoutFrame);
    });
    scheduleLayout();
  };

  const setup = (scope = document) => {
    const roots = [];
    if (scope.matches?.('[data-charles-stanley-offer]')) roots.push(scope);
    scope.querySelectorAll?.('[data-charles-stanley-offer]').forEach((root) => roots.push(root));

    roots.forEach((root) => {
      if (root.dataset.hbInteractionsReady === 'true') return;
      root.dataset.hbInteractionsReady = 'true';
      const teardowns = [];
      const addTeardown = (callback) => teardowns.push(callback);
      setupMobileMenu(root, addTeardown);
      root.querySelectorAll('[data-hb-gallery]').forEach(setupGallery);
      setupDisclosures(root);
      setupFooterDisclosures(root, addTeardown);
      setupCarousels(root, addTeardown);
      setupStickyPurchase(root, addTeardown);
      setupVideoAudio(root, addTeardown);
      setupReviewMasonry(root, addTeardown);
      setupOlivaDetails(root, addTeardown);
      rootTeardowns.set(root, () => {
        teardowns.reverse().forEach((teardown) => teardown());
        root.dataset.hbInteractionsReady = 'false';
      });
    });
  };

  const teardown = (scope) => {
    const roots = [];
    if (scope.matches?.('[data-charles-stanley-offer]')) roots.push(scope);
    scope.querySelectorAll?.('[data-charles-stanley-offer]').forEach((root) => roots.push(root));
    roots.forEach((root) => {
      rootTeardowns.get(root)?.();
      rootTeardowns.delete(root);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => setup(), { once: true });
  else setup();
  document.addEventListener('shopify:section:load', (event) => setup(event.target));
  document.addEventListener('shopify:section:unload', (event) => teardown(event.target));
})();
